import { z } from 'zod';

/**
 * Schema base para entidades do Firebird
 * AIDEV-NOTE: Todas entidades sincronizadas têm DATA_ULTIMA_ALTERACAO
 */
export const firebirdEntityBaseSchema = z.object({
  DATA_ULTIMA_ALTERACAO: z.string().datetime().nullable()
});

/**
 * Vendedor (VENDEDORES)
 */
export const vendedorSchema = firebirdEntityBaseSchema.extend({
  CODIGO: z.number().int().positive(),
  NOME: z.string(),
  EMAIL: z.string().email(),
  ATIVO: z.enum(['S', 'N']),
  TABELA_PRECO: z.number().int().positive().nullable(),
  CODIGO_PREPOSTO: z.number().int().positive().nullable(),
  DATA_CADASTRO: z.string().date().nullable()
});

export type Vendedor = z.infer<typeof vendedorSchema>;

/**
 * Cliente (CLIENTES)
 */
export const clienteSchema = firebirdEntityBaseSchema.extend({
  CODIGO: z.number().int().positive(),
  RAZAO_SOCIAL: z.string(),
  NOME_FANTASIA: z.string().nullable(),
  CNPJ_CPF: z.string(),
  INSCRICAO_ESTADUAL: z.string().nullable(),
  ENDERECO: z.string(),
  NUMERO: z.string(),
  COMPLEMENTO: z.string().nullable(),
  BAIRRO: z.string(),
  CIDADE: z.string(),
  UF: z.string().length(2),
  CEP: z.string(),
  TELEFONE: z.string().nullable(),
  CELULAR: z.string().nullable(),
  EMAIL: z.string().email().nullable(),
  CONTATO: z.string().nullable(),
  LIMITE_CREDITO: z.number().nonnegative().nullable(),
  SALDO_DEVEDOR: z.number().nonnegative().nullable(),
  SITUACAO: z.enum(['A', 'S']).nullable(), // A=Ativo, S=Suspenso
  LATITUDE: z.number().nullable(),
  LONGITUDE: z.number().nullable()
});

export type Cliente = z.infer<typeof clienteSchema>;

/**
 * Produto via VW_PRODUTOS_INTEGRACOES
 * AIDEV-NOTE: Substitui o antigo produtoSchema (PRODUTOS table). A view produz uma linha
 * por (CODIGO, CODCOR, CODTAMANHO, CODTABELAPRECO) — múltiplas por produto com variantes.
 */
export const produtoIntegracaoSchema = z.object({
  CODIGO: z.number().int().positive(),
  DESCRICAO: z.string(),
  CODCOR: z.number().int().default(0),
  COR: z.string().nullable(),
  CODTAMANHO: z.number().int().default(0),
  TAMANHO: z.string().nullable(),
  SECAO: z.string().nullable(),
  GRUPO: z.string().nullable(),
  SUBGRUPO: z.string().nullable(),
  CODTABELAPRECO: z.number().int().default(0),
  TABELA_PRECO: z.string().nullable(),
  PRECO_CUSTO: z.number().nonnegative().nullable(),
  PRECO_VENDA: z.number().nonnegative().nullable(),
  PRECO_ATACADO: z.number().nonnegative().nullable(),
  PRECO_PROMOCIONAL: z.number().nonnegative().nullable(),
  DATA_INICIO_PROMOCAO: z.string().nullable(),
  DATA_FIM_PROMOCAO: z.string().nullable(),
  DATA_INCLUSAO_PRODUTO: z.string().nullable(),
  DATA_ALTERACAO_PRODUTO: z.string().nullable(),
  DATA_INCLUSAO_PRECO: z.string().nullable(),
  DATA_ALTERACAO_PRECO: z.string().nullable(),
  SALDO: z.number().nullable(),
  DATA_ALTERACAO_ESTOQUE: z.string().nullable(),
  // AIDEV-NOTE: CODBARRA, PESO_LIQUIDO e PESO_BRUTO adicionados para rastreabilidade e logística
  CODBARRA: z.string().nullable(),
  PESO_LIQUIDO: z.number().nonnegative().nullable(),
  PESO_BRUTO: z.number().nonnegative().nullable()
});

export type ProdutoIntegracao = z.infer<typeof produtoIntegracaoSchema>;

/** @deprecated Use ProdutoIntegracao (VW_PRODUTOS_INTEGRACOES). Kept for type compatibility. */
export const produtoSchema = produtoIntegracaoSchema;
export type Produto = ProdutoIntegracao;

/**
 * Tabela de Preços simples (TABELA_PRECO) — lookup de nome para sync_tabelas_preco
 * AIDEV-NOTE: Fonte Firebird: tabela TABELA_PRECO (CODIGO, DESCRICAO)
 */
export const tabelaPrecoSyncSchema = z.object({
  CODIGO: z.number().int().positive(),
  DESCRICAO: z.string().nullable()
});
export type TabelaPrecoSync = z.infer<typeof tabelaPrecoSyncSchema>;

/**
 * Condição de Pagamento — estrutura Firebird (CONDICAO_PAGTO)
 * AIDEV-NOTE: Prefixo Firebird* para distinguir de CondicaoPagto em pedido.schemas.ts
 */
export const firebirdCondicaoPagtoSchema = z.object({
  CODIGO: z.number().int().positive(),
  DESCRICAO: z.string().nullable(),
  QTDE_PARCELAS: z.number().int().nonnegative().nullable(),
  ACRESCIMO: z.number().nonnegative().nullable(),
  DESCONTO: z.number().nonnegative().nullable()
});
export type FirebirdCondicaoPagto = z.infer<typeof firebirdCondicaoPagtoSchema>;

/**
 * Parcelas da Condição de Pagamento — estrutura Firebird (CONDICAO_PAGTO_PARCELAS)
 * AIDEV-NOTE: PK composta: (CODIGO, PARCELA) → (condpagto_erp_id, parcela) no Supabase
 */
export const firebirdCondicaoPagtoParcelasSchema = z.object({
  CODIGO: z.number().int().positive(),
  PARCELA: z.number().int().positive(),
  TIPO: z.number().int().nullable(),
  DIAS: z.number().int().nonnegative().nullable()
});
export type FirebirdCondicaoPagtoParcelas = z.infer<typeof firebirdCondicaoPagtoParcelasSchema>;

/**
 * Empresa (EMPRESA)
 * AIDEV-NOTE: Fonte Firebird: EMPRESA. Colunas confirmadas pelo DBA devem ser validadas.
 * cidade_erp_id mapeia para CODIGO_CIDADE (FK à tabela CIDADE).
 */
export const empresaSchema = z.object({
  CODIGO: z.number().int().positive(),
  RAZAO_SOCIAL: z.string().nullable(),
  NOME_FANTASIA: z.string().nullable(),
  CNPJ: z.string().nullable(),
  INSCRICAO_ESTADUAL: z.string().nullable(),
  INSCRICAO_MUNICIPAL: z.string().nullable(),
  ENDERECO: z.string().nullable(),
  NUMERO: z.number().int().nullable(),
  BAIRRO: z.string().nullable(),
  CODIGO_CIDADE: z.number().int().nullable(),
  ESTADO: z.string().nullable(),
  CEP: z.string().nullable(),
  FONE: z.string().nullable(),
  FAX: z.string().nullable(),
  CONTATO: z.string().nullable(),
  INSCRICAO_SUFRAMA: z.string().nullable(),
  CRT: z.number().int().nullable(),
  MATRIZ: z.string().nullable(),
  EMAIL: z.string().nullable(),
  COMPLEMENTO: z.string().nullable()
});
export type Empresa = z.infer<typeof empresaSchema>;

/**
 * Origem de Cliente (ORIGEM_CLIENTE)
 * AIDEV-NOTE: Fonte Firebird: ORIGEM_CLIENTE (CODIGO, DESCRICAO)
 */
export const origemClienteSchema = z.object({
  CODIGO: z.number().int().positive(),
  DESCRICAO: z.string().nullable()
});
export type OrigemCliente = z.infer<typeof origemClienteSchema>;

/**
 * Origem de Pedido — estrutura Firebird (ORIGEM_PEDIDO)
 * AIDEV-NOTE: Prefixo Firebird* para distinguir de OrigemPedido em pedido.schemas.ts
 */
export const firebirdOrigemPedidoSchema = z.object({
  CODIGO: z.number().int().positive(),
  DESCRICAO: z.string().nullable(),
  PONTO_ESTOQUE: z.number().int().nullable()
});
export type FirebirdOrigemPedido = z.infer<typeof firebirdOrigemPedidoSchema>;

/**
 * Ponto de Estoque (PONTO_ESTOQUE)
 * AIDEV-NOTE: Fonte Firebird: PONTO_ESTOQUE (CODIGO INTEGER, DESCRICAO VARCHAR(50))
 */
export const firebirdPontoEstoqueSchema = z.object({
  CODIGO: z.number().int(),
  DESCRICAO: z.string().nullable()
});
export type FirebirdPontoEstoque = z.infer<typeof firebirdPontoEstoqueSchema>;

/**
 * Saldo de estoque por ponto (agregação de MOV_ESTOQUE)
 * AIDEV-NOTE: PONTO_ESTOQUE = -1 representa movimento sem ponto definido no ERP.
 */
export const firebirdSaldoPontoSchema = z.object({
  PRODUTO: z.number().int(),
  COLECAO: z.number().int(),
  TAMANHO: z.number().int(),
  PONTO_ESTOQUE: z.number().int(),
  SALDO: z.number(),
  DATA_ALTERACAO_ESTOQUE: z.string().nullable()
});
export type FirebirdSaldoPonto = z.infer<typeof firebirdSaldoPontoSchema>;

/**
 * Ramo de Atividade (RAMO_ATIVIDADE)
 * AIDEV-NOTE: Fonte Firebird: RAMO_ATIVIDADE (CODIGO, DESCRICAO)
 */
export const ramoAtividadeSchema = z.object({
  CODIGO: z.number().int().positive(),
  DESCRICAO: z.string().nullable()
});
export type RamoAtividade = z.infer<typeof ramoAtividadeSchema>;

/**
 * Tabela de Preços (TABELAS_PRECO)
 */
export const tabelaPrecoSchema = firebirdEntityBaseSchema.extend({
  CODIGO: z.number().int().positive(),
  DESCRICAO: z.string(),
  DATA_INICIO: z.string().date().nullable(),
  DATA_FIM: z.string().date().nullable(),
  STATUS: z.enum(['A', 'I']).nullable()
});

export type TabelaPreco = z.infer<typeof tabelaPrecoSchema>;

/**
 * Item da Tabela de Preços (ITENS_TABELA_PRECO)
 */
export const itemTabelaPrecoSchema = firebirdEntityBaseSchema.extend({
  CODIGO_TABELA: z.number().int().positive(),
  CODIGO_PRODUTO: z.number().int().positive(),
  PRECO: z.number().nonnegative(),
  DESCONTO_MAXIMO: z.number().nonnegative().nullable()
});

export type ItemTabelaPreco = z.infer<typeof itemTabelaPrecoSchema>;

/**
 * Pedido (PEDIDOS) - Estrutura Firebird
 */
export const pedidoFirebirdSchema = firebirdEntityBaseSchema.extend({
  NUMERO: z.number().int().positive(),
  SERIE: z.string(),
  DATA_EMISSAO: z.string().date(),
  DATA_SAIDA: z.string().date().nullable(),
  CODIGO_CLIENTE: z.number().int().positive(),
  CODIGO_VENDEDOR: z.number().int().positive(),
  CODIGO_TABELA_PRECO: z.number().int().positive(),
  VALOR_TOTAL: z.number().nonnegative(),
  DESCONTO_TOTAL: z.number().nonnegative().nullable(),
  FRETE: z.number().nonnegative().nullable(),
  OUTRAS_DESPESAS: z.number().nonnegative().nullable(),
  SITUACAO: z.enum(['E', 'C', 'F']).nullable(), // E=Em aberto, C=Cancelado, F=Fechado
  OBSERVACOES: z.string().nullable()
});

export type PedidoFirebird = z.infer<typeof pedidoFirebirdSchema>;

/**
 * Item do Pedido (ITENS_PEDIDO)
 */
export const itemPedidoSchema = firebirdEntityBaseSchema.extend({
  NUMERO_PEDIDO: z.number().int().positive(),
  SERIE_PEDIDO: z.string(),
  SEQUENCIA: z.number().int().positive(),
  CODIGO_PRODUTO: z.number().int().positive(),
  DESCRICAO_PRODUTO: z.string(),
  QUANTIDADE: z.number().positive(),
  UNIDADE: z.string(),
  PRECO_UNITARIO: z.number().nonnegative(),
  DESCONTO: z.number().nonnegative().nullable(),
  VALOR_TOTAL: z.number().nonnegative(),
  CFOP: z.string().nullable(),
  CST: z.string().nullable(),
  ALIQUOTA_IPI: z.number().nonnegative().nullable(),
  ALIQUOTA_ICMS: z.number().nonnegative().nullable()
});

export type ItemPedido = z.infer<typeof itemPedidoSchema>;
